import { CHECKOUT_ITEM_TYPE_EXPERIENCE, CHECKOUT_ITEM_TYPE_TRANSFER } from 'constants/checkout'
import { sum } from 'lib/array/arrayUtils'
import uuidV4 from 'lib/string/uuidV4Utils'

export interface ExperienceTicketCount {
  ticket: App.ExperienceItemTicket
  count: number
}

export interface ExperienceDetails {
  date?: string
  time?: string
  language?: string
  pickupPoint?: string
  redemptionLocation?: string
  ticketModesMap?: Record<string, string>
}

export function generateExperienceCheckoutTicket(item: ExperienceTicketCount): App.Checkout.ExperienceItemTicket {
  const ticket = item.ticket
  return {
    ticketId: ticket.id,
    name: ticket.name,
    count: item.count,
    price: ticket.price,
    memberPrice: ticket.memberPrice,
    discount: ticket.discounts.app.amount,
    date: ticket.date,
    orderItemIds: ticket.orderItemIds,
  }
}

interface ExperienceItemOptions {
  isBookingBNBL?: boolean
  cartItem?: App.Checkout.ExperienceItem
  isBuyNowBookLater?: boolean
  isGift?: boolean
  orderMode?: App.ExperienceOrderMode
  isAccomBundle?: string
  experiencesPassport?: boolean
  accommodationParentItemId?: string
  metaData?: Record<string, unknown>
}

export function generateExperienceCheckoutItem(
  experience: App.ExperienceOffer,
  details: ExperienceDetails,
  tickets: Array<ExperienceTicketCount>,
  options: ExperienceItemOptions = {},
): App.Checkout.ExperienceItem {
  const {
    cartItem,
    isBookingBNBL,
    isBuyNowBookLater,
    isGift,
    orderMode,
    isAccomBundle,
    metaData,
    experiencesPassport,
    accommodationParentItemId,
  } = options

  const validTickets = tickets.filter((ticket) => ticket.count > 0)
  const totalPrice = sum(validTickets, ticketCount => ticketCount.ticket.price)
  const memberTotal = sum(validTickets, ticketCount => ticketCount.ticket.memberPrice ?? 0)

  const item: App.Checkout.ExperienceItem = {
    itemId: cartItem?.itemId || uuidV4(),
    transactionKey: cartItem?.transactionKey || uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_EXPERIENCE,
    bookingType: experience.bookingType,
    tickets: validTickets.map(generateExperienceCheckoutTicket),
    experienceId: experience.id,
    leExclusive: experience.leExclusive,
    date: details.date,
    isBuyNowBookLater: isBuyNowBookLater ?? false,
    isGift: !!isGift,
    orderMode,
    isBookingBNBL,
    isAccomBundle,
    experiencesPassport,
    accommodationParentItemId,
    time: details.time,
    languageId: details.language,
    pickupPointId: details.pickupPoint,
    redemptionLocationId: details.redemptionLocation,
    ticketModesMap: details.ticketModesMap,
    metaData,
    total: totalPrice,
    memberTotal,
  }

  return item
}

const EXPERIENCES_PASSPORT_BOOKING_TYPES: Array<App.ExperienceOffer['bookingType']> = [
  'NO-CALENDAR-FIXED-END',
  'NO-CALENDAR-FIXED-VALIDITY',
]

export function isEligibleExperiencesPassportTicket(ticket: App.ExperienceItemTicket): boolean {
  return ticket.price === 0 && !ticket.isExtra && ticket.purchaseLimit.max > 0
}

export function generateExperiencesPassportCheckoutItems(
  accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem>,
  checkoutSources: Array<{ experience: App.ExperienceOffer, tickets: Array<App.ExperienceItemTicket> }>,
): Array<App.Checkout.ExperienceItem> {
  if (!accommodationItems.length) return []

  return checkoutSources
    .filter(({ experience }) => experience.isExperiencePassport && EXPERIENCES_PASSPORT_BOOKING_TYPES.includes(experience.bookingType))
    .flatMap(({ experience, tickets }) =>
      // Each accommodation item represents an entitled room, so every room receives its own voucher.
      accommodationItems.map((accommodationItem) => {
        const ticket = tickets.find(isEligibleExperiencesPassportTicket)
        if (!ticket) return undefined

        return generateExperienceCheckoutItem(
          experience,
          {
            date: undefined,
            redemptionLocation: experience.redemptionLocations[0]?.id,
          },
          [{
            ticket,
            count: ticket.purchaseLimit.min || 1,
          }],
          {
            experiencesPassport: true,
            accommodationParentItemId: accommodationItem.itemId,
          },
        )
      }),
    )
    .filter((item): item is App.Checkout.ExperienceItem => !!item)
}

interface BundledTransferInput {
  experience: App.ExperienceOffer
  option: App.ExperienceTransferOption
  date: string
  hotel: App.ExperienceTransferView['hotel']
  travellers: App.ExperienceTransferTravellers
  baggage: App.OrderTransferItemBaggage
}

export function generateBundledAccommodationExperienceItem(
  accommodationItemIds: Array<string>,
  bundleId: string,
  ticket: { experienceId: string; productId: string; price: number },
): App.Checkout.ExperienceItem
export function generateBundledAccommodationExperienceItem(
  accommodationItemIds: Array<string>,
  bundleId: string,
  ticket: { experienceId: string; productId: string; price: number },
  transferData: BundledTransferInput,
): App.Checkout.TransferItem
export function generateBundledAccommodationExperienceItem(
  accommodationItemIds: Array<string>,
  bundleId: string,
  ticket: { experienceId: string; productId: string; price: number },
  transferData?: BundledTransferInput,
): App.Checkout.ExperienceItem | App.Checkout.TransferItem {
  if (transferData) {
    const { experience, option, date, hotel, travellers, baggage } = transferData
    const transferItem = generateTransferCheckoutItem(experience, {
      option: {
        ...option,
        price: ticket.price,
        memberPrice: 0,
        discounts: {
          app: {
            amount: 0,
            percentage: 0,
          },
          base: {
            amount: 0,
            percentage: 0,
          },
        },
      },
      date,
      time: '',
      flightNumber: '',
      title: experience.name,
      type: option.transferTypes[0],
      isHomePickup: false,
      hotel,
      travellers,
      baggage,
    })
    return {
      ...transferItem,
      total: ticket.price,
      bundledItemIds: accommodationItemIds,
      isAccomBundle: bundleId,
    }
  }
  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_EXPERIENCE,
    total: ticket.price,
    experienceId: ticket.experienceId,
    tickets: [{
      ticketId: ticket.productId,
      name: 'Bundled experience',
      count: 1,
      price: ticket.price,
      memberPrice: 0,
      discount: 0,
    }],
    leExclusive: false,
    isBuyNowBookLater: false,
    isGift: false,
    bundledItemIds: accommodationItemIds,
    isAccomBundle: bundleId,
  }
}

export function generateTransferCheckoutItem(
  experience: App.ExperienceOffer,
  transfer: App.ExperienceTransferView,
  cartItem?: App.Checkout.TransferItem,
): App.Checkout.TransferItem {
  const item: App.Checkout.TransferItem = {
    itemId: cartItem?.itemId || uuidV4(),
    transactionKey: cartItem?.transactionKey || uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_TRANSFER,
    transfer,
    experienceId: experience.id,
    offerType: experience.productType,
    hasLoyaltyBenefit: cartItem?.hasLoyaltyBenefit,
    isLoyaltyTransfer: cartItem?.isLoyaltyTransfer,
  }

  return item
}

export function generateLoyaltyLoungePassItem(
  experience: App.ExperienceOffer,
  ticket: App.ExperienceItemTicket,
  options: {
    date?: string
    time?: string
    isBookLater?: boolean
    isFree?: boolean
  } = {},
): App.Checkout.LoyaltyLoungePassItem {
  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: 'loungePassItem',
    experienceId: experience.id,
    ticketId: ticket.id,
    isBookLater: !!options.isBookLater,
    isFree: !!options.isFree,
    date: options.date,
    time: options.time,
    redemptionLocation: experience.redemptionLocations[0].id,
  }
}
